import { Moment } from 'moment';
import * as yup from 'yup';
import moment from 'moment';

interface Interval {
	timeFrom?: Moment;
	timeTo?: Moment;
}

const INTERVAL_MIN_ALLOWED = 7 * 60; // 07:00
const INTERVAL_MAX_ALLOWED = 19 * 60; // 19:00
const INTERVAL_MAX_TOTAL_MINUTES = 6 * 60; // 6 hours

const toMinutes = (time: Moment | string) => {
	const m = typeof time === 'string' ? moment(time, 'HH:mm', true) : time;

	if (!m || typeof m.hour !== 'function') {
		return null;
	}

	return m.hour() * 60 + m.minute();
};

const validateIntervalLimits = (interval: Interval) => {
	if (!interval?.timeFrom || !interval?.timeTo) {
		return true;
	}

	const fromMin = toMinutes(interval.timeFrom);
	const toMin = toMinutes(interval.timeTo);

	if (fromMin === null || toMin === null) {
		return true;
	}

	return (
		fromMin >= INTERVAL_MIN_ALLOWED &&
		fromMin <= INTERVAL_MAX_ALLOWED &&
		toMin >= INTERVAL_MIN_ALLOWED &&
		toMin <= INTERVAL_MAX_ALLOWED
	);
};

const validateIntervalTotal = (intervals?: Interval[]) => {
	if (!Array.isArray(intervals) || intervals.length === 0) {
		return true;
	}

	const totalMinutes = intervals.reduce((sum, item: any) => {
		const fromMin = toMinutes(item?.timeFrom);
		const toMin = toMinutes(item?.timeTo);

		if (fromMin === null || toMin === null) {
			return sum;
		}

		return sum + Math.max(0, toMin - fromMin);
	}, 0);

	return totalMinutes <= INTERVAL_MAX_TOTAL_MINUTES;
};

export const CSSZSickLeavePatientAddressValidationScheme = yup.object({
	street: yup.string().nullable(),
	descriptiveNumber: yup
		.string()
		.required('validation.CSSZSickLeavePatientAddressForm.descriptiveNumber'),
	guideNumber: yup.string().nullable(),
	city: yup
		.string()
		.required('validation.CSSZSickLeavePatientAddressForm.city'),
	postCode: yup
		.string()
		.trim()
		.required('validation.CSSZSickLeavePatientAddressForm.postCode')
		.matches(
			/^\d{5}$/,
			'validation.CSSZSickLeavePatientAddressForm.postCodeMatch',
		),
	typeOfAddress: yup
		.object({
			value: yup.string(),
			label: yup.string(),
		})
		.nullable(),
	state: yup
		.object({
			value: yup
				.string()
				.required('validation.CSSZSickLeavePatientAddressForm.state'),
			label: yup.string(),
		})
		.required('validation.CSSZSickLeavePatientAddressForm.state')
		.typeError('validation.CSSZSickLeavePatientAddressForm.state'),
	floor: yup.string().nullable(),
	partOfCity: yup.string().nullable(),
	note: yup.string().nullable(),
});

export const CSSZPermittedWalksTimeRangeValidationSchema = yup
	.object({
		timeFrom: yup
			.mixed<Moment>()
			.required(
				'validation.CSSZPermittedWalksTimeRangeValidationSchema.timeFrom',
			),
		timeTo: yup
			.mixed<Moment>()
			.required(
				'validation.CSSZPermittedWalksTimeRangeValidationSchema.timeTo',
			),
	})
	.required()
	.test(
		'within-07-19',
		'validation.CSSZPermittedWalksTimeRangeValidationSchema.outsideAllowedTimeRange',
		validateIntervalLimits,
	)
	.test(
		'from-before-to',
		'validation.CSSZPermittedWalksTimeRangeValidationSchema.timesValidation',
		value => {
			if (!value?.timeFrom || !value?.timeTo) return true;
			const from = value.timeFrom;
			const to = value.timeTo;
			if (typeof (from as any).isBefore === 'function') {
				return (from as any).isBefore(to);
			}
			return true;
		},
	);

const isOutingIntervalFilled = (item: Record<string, string>): boolean => {
	return Boolean(item?.timeFrom || item?.timeTo);
};

export const hasOutingsData = (
	outings?: {
		outingsStartDate?: string | null;
		outingIntervals?: unknown[];
	} | null,
): boolean => {
	if (!outings) {
		return false;
	}

	if (outings.outingsStartDate) {
		return true;
	}

	return (
		Array.isArray(outings.outingIntervals) &&
		outings.outingIntervals.some((item: any) =>
			isOutingIntervalFilled(item ?? {}),
		)
	);
};

const hasAnyFilledInterval = (items: unknown): boolean => {
	if (!Array.isArray(items)) return false;
	return items.some((i: any) => isOutingIntervalFilled(i ?? {}));
};

export const CSSZPermittedWalksValidationSchema = yup
	.object({
		outingsStartDate: yup.string().nullable(),
		outingIntervals: yup
			.array()
			.transform((_value, originalValue) => {
				if (!Array.isArray(originalValue)) return [];
				return originalValue.filter(isOutingIntervalFilled);
			})
			.of(CSSZPermittedWalksTimeRangeValidationSchema)
			.test(
				'max-total-6h',
				'validation.CSSZPermittedWalksValidationSchema.maxTotalDuration',
				validateIntervalTotal,
			),
	})
	.test(
		'outings-consistent',
		'validation.CSSZPermittedWalksValidationSchema.outingsStartDate',
		function (value: any) {
			const start = value?.outingsStartDate ?? null;
			const intervals = Array.isArray(value?.outingIntervals)
				? value.outingIntervals
				: [];
			const hasDate = Boolean(start);
			const hasIntervals = hasAnyFilledInterval(intervals);

			if (!hasDate && !hasIntervals) {
				return true;
			}

			if (hasIntervals && !hasDate) {
				return this.createError({
					path: `${this.path}.outingsStartDate`,
					message:
						'validation.CSSZPermittedWalksValidationSchema.outingsStartDate',
				});
			}

			if (hasDate && intervals.length < 1) {
				return this.createError({
					path: `${this.path}.outingIntervals`,
					message:
						'validation.CSSZPermittedWalksValidationSchema.atLeastOneInterval',
				});
			}

			return true;
		},
	)
	.transform((value, originalValue) => {
		const start = originalValue?.outingsStartDate ?? null;
		const intervals = originalValue?.outingIntervals ?? [];
		const hasDate = Boolean(start);
		const hasIntervals = hasAnyFilledInterval(intervals);

		if (!hasDate && !hasIntervals) {
			return null;
		}

		return value;
	})
	.nullable()
	.noUnknown();
